import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiHandler from '../services/apiServices';

const SLACK_CLIENT_ID = import.meta.env.VITE_SLACK_CLIENT_ID;
const SLACK_REDIRECT_URI = import.meta.env.VITE_SLACK_REDIRECT_URI;

interface SlackStatus {
  connected: boolean;
  workspace_name?: string;
  channel_id?: string;
  channel_name?: string;
}

interface Channel {
  id: string;
  name: string;
  is_private: boolean;
}

const slackService = {
  getStatus: async (): Promise<SlackStatus> => {
    const response = await apiHandler.get('/slack/status');
    return response.data;
  },

  disconnect: async () => {
    const response = await apiHandler.delete('/slack/disconnect');
    return response.data;
  },

  completeOAuth: async (code: string): Promise<void> => {
    const response = await apiHandler.post('/slack/oauth/callback', { code });
    return response.data;
  },

  getChannels: async (): Promise<Channel[]> => {
    try {
      const response = await apiHandler.get('/slack/channels');
      return response.data.channels || [];
    } catch (error) {
      console.error('Error fetching slack channels:', error);
      return [];
    }
  },

  setChannel: async (channelId: string): Promise<void> => {
    const response = await apiHandler.post('/slack/set-channel', { channel_id: channelId });
    return response.data;
  }
};

export function useSlackIntegration() {
  const queryClient = useQueryClient();

  const { data: status = { connected: false }, isLoading } = useQuery({
    queryKey: ['slackIntegration'],
    queryFn: slackService.getStatus,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
  });

  const { data: channels = [], isLoading: isLoadingChannels } = useQuery({
    queryKey: ['slackChannels'],
    queryFn: slackService.getChannels,
    enabled: status.connected,
    staleTime: 1000 * 60 * 5, // 5 minutes
    select: (data) => Array.isArray(data) ? data : [],
  });

  const disconnectMutation = useMutation({
    mutationFn: slackService.disconnect,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slackIntegration'] });
    },
  });

  const handleOAuthCallback = useMutation({
    mutationFn: slackService.completeOAuth,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slackIntegration'] });
    },
  });

  const setChannelMutation = useMutation({
    mutationFn: slackService.setChannel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slackIntegration'] });
    },
  });

  const initiateSlackConnection = () => {
    const scope = 'chat:write,channels:read,channels:join';
    const url = `https://slack.com/oauth/v2/authorize?client_id=${SLACK_CLIENT_ID}&scope=${scope}&redirect_uri=${SLACK_REDIRECT_URI}`;
    window.location.href = url;
  };

  return {
    status,
    channels,
    isLoading,
    isLoadingChannels,
    disconnect: disconnectMutation.mutate,
    handleOAuthCallback,
    initiateSlackConnection,
    setChannel: setChannelMutation.mutate,
    isDisconnecting: disconnectMutation.isPending,
    isConnecting: handleOAuthCallback.isPending,
    isSettingChannel: setChannelMutation.isPending,
  };
}
